/**
 * 密文字体加载失败日志（仅本地开发启用内存缓冲，生产环境不写内存）。
 *
 * 本地读取方式：
 * - Admin DevTools: window.__cipherFontLoadReport()
 * - 小程序 DevTools: getApp().globalData.__cipherFontLoadReport()
 * - 清空: window.__cipherFontLoadClear?.() / getApp().globalData.__cipherFontLoadClear?.()
 */
declare const __CIPHER_FONT_DEBUG__: boolean | undefined;

const MAX_FAILURE_LOGS = 500;
const MAX_SUMMARY_LOGS = 50;

export type CipherFontLoadFailureKind =
  | 'css_fetch'
  | 'css_inject'
  | 'woff2_load'
  | 'load_font_face'
  | 'glyph_probe';

export interface CipherFontLoadFailure {
  kind: CipherFontLoadFailureKind;
  message: string;
  platform: 'web' | 'mp';
  family?: string;
  url?: string;
  codepoint?: number;
  char?: string;
  detail?: unknown;
  at: string;
}

export interface CipherFontLoadSummary {
  platform: 'web' | 'mp';
  cdnBase?: string;
  totalFaces?: number;
  failedFaces?: number;
  probedGlyphs?: number;
  missingGlyphs?: number;
  at: string;
}

/** 仅 development 构建注入 __CIPHER_FONT_DEBUG__=true 时为 true */
export function isCipherFontDebugEnabled(): boolean {
  return typeof __CIPHER_FONT_DEBUG__ !== 'undefined' && __CIPHER_FONT_DEBUG__ === true;
}

const failures: CipherFontLoadFailure[] = [];
const summaries: CipherFontLoadSummary[] = [];

function pushFailure(entry: CipherFontLoadFailure): void {
  if (!isCipherFontDebugEnabled()) return;
  failures.push(entry);
  if (failures.length > MAX_FAILURE_LOGS) {
    failures.splice(0, failures.length - MAX_FAILURE_LOGS);
  }
}

function pushSummary(summary: CipherFontLoadSummary): void {
  if (!isCipherFontDebugEnabled()) return;
  summaries.push(summary);
  if (summaries.length > MAX_SUMMARY_LOGS) {
    summaries.splice(0, summaries.length - MAX_SUMMARY_LOGS);
  }
}

function logToConsole(entry: CipherFontLoadFailure): void {
  if (!isCipherFontDebugEnabled()) return;
  console.warn(
    `[cipher-font][${entry.platform}][${entry.kind}]`,
    entry.message,
    entry.family ?? '',
    entry.url ?? '',
    entry.codepoint !== undefined ? `U+${entry.codepoint.toString(16).toUpperCase()}` : '',
    entry.detail ?? '',
  );
}

/** 记录单次失败（本地 dev：内存 + console；生产：忽略） */
export function recordCipherFontLoadFailure(
  entry: Omit<CipherFontLoadFailure, 'at'>,
): void {
  const full = { ...entry, at: new Date().toISOString() };
  pushFailure(full);
  logToConsole(full);
}

/** 记录一批加载汇总（仅本地 dev 内存） */
export function recordCipherFontLoadSummary(summary: Omit<CipherFontLoadSummary, 'at'>): void {
  const full = { ...summary, at: new Date().toISOString() };
  pushSummary(full);
  if (
    isCipherFontDebugEnabled() &&
    ((summary.failedFaces ?? 0) > 0 || (summary.missingGlyphs ?? 0) > 0)
  ) {
    console.warn('[cipher-font] load summary', full);
  }
}

export function getCipherFontLoadFailures(): readonly CipherFontLoadFailure[] {
  return failures;
}

export function getCipherFontLoadSummaries(): readonly CipherFontLoadSummary[] {
  return summaries;
}

export function clearCipherFontLoadLogs(): void {
  failures.length = 0;
  summaries.length = 0;
}

export function getCipherFontLoadReport(): {
  failures: readonly CipherFontLoadFailure[];
  summaries: readonly CipherFontLoadSummary[];
  debugEnabled: boolean;
} {
  return { failures, summaries, debugEnabled: isCipherFontDebugEnabled() };
}
